import React, { useEffect, useState } from "react"
import { ButtonBase, Grid } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import ServiceCard from "./service-card"

type CardItem = React.ComponentProps<typeof ServiceCard>["service"]

const useStyles = makeStyles(theme => ({
  dots: {
    display: "flex",
    justifyContent: "center",
    marginTop: 20,
  },
  dotButton: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "rgba(35, 51, 72, 0.22)",
  },
  activeDot: {
    backgroundColor: theme.palette.primary.main,
    transform: "scale(1.25)",
  },
}))

const CardSlideshow = ({ items, label }: { items: CardItem[]; label: string }) => {
  const classes = useStyles()
  const [page, setPage] = useState(0)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const pages = Math.ceil(items.length / 3)

  // Start a fresh countdown after navigation; pause while someone uses a card.
  useEffect(() => {
    if (pages < 2 || hovered || focused) return
    const timer = window.setTimeout(() => {
      setPage(current => (current + 1) % pages)
    }, 15000)
    return () => window.clearTimeout(timer)
  }, [page, pages, hovered, focused])

  if (!items.length) return null
  const currentPage = page % pages
  // Wrap the final group so every slide has three cards when available.
  const visible = Array.from(
    { length: Math.min(3, items.length) },
    (_, offset) => items[(currentPage * 3 + offset) % items.length]
  )

  return (
    <Grid item xs={12}>
      <div
        role="region"
        aria-roledescription="carousel"
        aria-label={label}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setFocused(false)
          }
        }}
      >
        <Grid container spacing={3}>
          {visible.map(item => (
            <Grid item xs={12} sm={4} key={item.url}>
              <ServiceCard service={item} />
            </Grid>
          ))}
        </Grid>
        {pages > 1 && (
          <div className={classes.dots} role="group" aria-label={`${label} slides`}>
            {Array.from({ length: pages }, (_, index) => (
              <ButtonBase
                key={index}
                className={classes.dotButton}
                aria-label={`Show slide ${index + 1} of ${pages}`}
                aria-current={index === currentPage ? "true" : undefined}
                onClick={() => setPage(index)}
              >
                <span
                  className={`${classes.dot} ${index === currentPage ? classes.activeDot : ""}`}
                />
              </ButtonBase>
            ))}
          </div>
        )}
      </div>
    </Grid>
  )
}

export default CardSlideshow
